import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { Toaster, ToastBar, toast } from 'react-hot-toast'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import App from './App.tsx'
import './index.css'
import './services/apiClient' // Initialize API client configuration


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: Error & { status?: number }) => {
        // stop on auth errors
        if (error.status === 401) return false
        // never retry server errors
        if (typeof error.status === 'number' && error.status >= 500) return false
        // allow at most one retry for transient cases
        return failureCount < 2
      },
    },
  },
})

const toastStyle = {
  background: '#363636',
  color: '#fff',
}

const successIconTheme = {
  primary: '#4ade80',
  secondary: '#fff',
}

const errorIconTheme = {
  primary: '#ef4444',
  secondary: '#fff',
}

const successOptions = {
  duration: 3000,
  iconTheme: successIconTheme,
}

const errorOptions = {
  duration: 5000,
  iconTheme: errorIconTheme,
}

const toastOptions = {
  duration: 4000,
  style: toastStyle,
  success: successOptions,
  error: errorOptions,
} as const

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <DndProvider backend={HTML5Backend}>
          <App />
          <Toaster
            position="top-right"
            toastOptions={toastOptions}
          >
            {(t) => (
              <ToastBar 
                toast={t}
                style={{
                  ...t.style,
                  maxWidth: '450px',
                }}
              >
                {({ icon, message }) => (
                  <>
                    {icon}
                    <span style={{ 
                      flex: 1,
                      fontSize: '14px',
                      lineHeight: '1.4',
                      paddingRight: t.type !== 'loading' ? '8px' : '0',
                    }}>{message}</span>
                    {t.type !== 'loading' && (
                      <button
                        type="button"
                        onClick={() => { toast.dismiss(t.id) }}
                        style={{
                          marginLeft: 'auto',
                          marginRight: '8px',
                          background: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          cursor: 'pointer',
                          color: '#9ca3af',
                          padding: '3px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          transition: 'all 0.2s',
                          minWidth: '24px',
                          height: '24px',
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'
                          e.currentTarget.style.color = '#ffffff'
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'
                          e.currentTarget.style.color = '#9ca3af'
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)'
                        }}
                        aria-label="Dismiss notification"
                      >
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 14 14" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M1.5 1.5L12.5 12.5M1.5 12.5L12.5 1.5" 
                            stroke="currentColor" 
                            strokeWidth="1.5" 
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </ToastBar>
            )}
          </Toaster>
        </DndProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
